// workspace-store.ts — État des tuiles côté client.
//
// Deux besoins qui n'en font qu'un : le volet doit s'ouvrir sans latence, et
// le compteur doit se décrémenter sous les yeux quand on clôt une action. Les
// deux se règlent en gardant les données en mémoire plutôt qu'en les
// rechargeant à l'ouverture — le volet n'a donc pas besoin d'être collant pour
// être instantané.
//
// Invalidation ciblée : chaque tuile déclare, via le catalogue, les entités
// qui la périment (`invalidated_by`). Une mutation émet `workspace:invalidate`
// depuis le client d'API (point de passage unique), le store recoupe, et
// SEULES les tuiles concernées sont rechargées via `?tiles=…`. Sans cette
// déclaration on rechargerait tout à chaque enregistrement, ce qui annulerait
// le budget de requêtes du back.

import type { CatalogEntry, WorkspaceTile } from '../api/workspace.js';

type Listener = () => void;

const listeners = new Set<Listener>();

let tiles: WorkspaceTile[] = [];
let catalog: CatalogEntry[] = [];
let loaded = false;
let loading: Promise<void> | null = null;

// Entités invalidantes par clé de tuile, dérivées du catalogue.
let invalidationMap: Record<string, string[]> = {};

// Tuiles dont le chiffre vient de bouger, et jusqu'à quand elles clignotent.
// Un rafraîchissement peut ne rien changer (on a enregistré une action qui
// n'entre dans aucun compteur) : ne clignote que ce qui a RÉELLEMENT bougé,
// sinon le signal perd son sens à force de se déclencher pour rien.
const flashUntil = new Map<number, number>();
const FLASH_MS = 1500;

export function isFlashing(tileId: number): boolean {
  return flashUntil.has(tileId);
}

/** Marque des tuiles comme « vient de changer », et programme leur extinction. */
function markFlash(ids: number[]): void {
  if (!ids.length) return;
  const until = Date.now() + FLASH_MS;
  for (const id of ids) flashUntil.set(id, until);
  emit();
  // Deux changements rapprochés sur la même tuile : le second repousse
  // l'échéance, et le réveil du premier ne l'éteint pas prématurément.
  window.setTimeout(() => {
    const now = Date.now();
    let expired = false;
    for (const [id, exp] of flashUntil) {
      if (exp <= now) {
        flashUntil.delete(id);
        expired = true;
      }
    }
    if (expired) emit();
  }, FLASH_MS + 50);
}

function emit(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch (e) {
      safeWarn('[workspace-store] listener', e);
    }
  }
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getTiles(): WorkspaceTile[] {
  return tiles;
}

export function getCatalog(): CatalogEntry[] {
  return catalog;
}

export function isLoaded(): boolean {
  return loaded;
}

/** Nombre porté par le badge de la pastille : l'urgent, pas le total. */
export function badgeCount(): number {
  const owner = tiles.find((t) => t.key === 'actions_owner');
  const n = owner?.data?.count;
  return typeof n === 'number' ? n : 0;
}

export async function load(force = false): Promise<void> {
  if (loaded && !force) return;
  if (loading && !force) return loading;
  loading = (async () => {
    try {
      const [tilesRes, catalogRes] = await Promise.all([
        api.getWorkspaceTiles(),
        api.getWorkspaceCatalog(),
      ]);
      tiles = tilesRes.tiles;
      catalog = catalogRes.catalog;
      invalidationMap = {};
      for (const entry of catalog) invalidationMap[entry.key] = entry.invalidated_by;
      loaded = true;
      emit();
    } finally {
      loading = null;
    }
  })();
  return loading;
}

export function setTiles(next: WorkspaceTile[]): void {
  tiles = next;
  loaded = true;
  emit();
}

/**
 * Recharge uniquement les tuiles que cette entité périme.
 *
 * Silencieux en cas d'échec : un rafraîchissement de tuiles ne doit jamais
 * interrompre le geste métier que l'utilisateur vient de faire. Le chiffre
 * restera simplement celui de la décrémentation optimiste jusqu'au prochain
 * passage.
 */
export async function invalidate(entity: string): Promise<void> {
  if (!loaded) return;
  const stale = tiles.filter((t) => (invalidationMap[t.key] || []).includes(entity));
  if (!stale.length) return;
  try {
    const res = await api.getWorkspaceTiles(stale.map((t) => t.id));
    const byId = new Map(res.tiles.map((t) => [t.id, t]));
    // Comparé AVANT le remplacement : c'est l'écart entre l'ancien et le
    // nouveau chiffre qui justifie le clignotement, pas le rafraîchissement.
    const moved = tiles
      .filter((t) => {
        const next = byId.get(t.id);
        return next !== undefined && JSON.stringify(next.data) !== JSON.stringify(t.data);
      })
      .map((t) => t.id);
    tiles = tiles.map((t) => byId.get(t.id) ?? t);
    if (moved.length) markFlash(moved);
    else emit();
  } catch (e) {
    safeWarn('[workspace-store] invalidate', e);
  }
}

/**
 * Décrémentation optimiste avant l'aller-retour serveur.
 *
 * C'est la « petite victoire » : clore une action fait bouger le compteur
 * immédiatement, pas 300 ms plus tard. Le rafraîchissement qui suit
 * réconcilie ; si le serveur contredit, le chiffre se corrige sans message —
 * un avertissement pour un compteur serait plus perturbant que l'écart.
 */
export function decrementOptimistic(tileKey: string, field: string, by = 1): void {
  const tile = tiles.find((t) => t.key === tileKey);
  if (!tile?.data) return;
  const current = tile.data[field];
  if (typeof current !== 'number') return;
  tile.data = { ...tile.data, [field]: Math.max(0, current - by) };
  tiles = [...tiles];
  // Le clignotement part d'ici, pas du rafraîchissement serveur : c'est le
  // geste de l'utilisateur qu'il accuse, et il doit arriver dans la foulée.
  markFlash([tile.id]);
}

/** Branche l'invalidation sur l'événement émis par le client d'API. */
export function wireInvalidation(): void {
  window.addEventListener('workspace:invalidate', (ev) => {
    void invalidate(ev.detail.entity);
  });
}
